// ============================================
// OFFICEFORGED — Card Library Panel
// Browse, search, create, and manage reusable
// cards. Insert cards into documents with one click.
// ============================================

export class CardPanel {
  constructor(cardEngine) {
    this.cardEngine = cardEngine;
    this.container = document.getElementById('card-list');
    this.countEl = document.getElementById('card-count');
    this.activeCardId = null;
    this._view = 'library'; // 'library' | 'document'
    this._typeFilter = '';
    this._searchQuery = '';

    // Listen for card changes
    this.cardEngine.on((event) => {
      this.render();
    });

    // Toggle button
    const toggleBtn = document.getElementById('btn-toggle-cards');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const panel = document.getElementById('card-panel');
        panel.classList.toggle('collapsed');
        toggleBtn.classList.toggle('active');
      });
    }

    // Build the library header UI
    this._buildHeader();
    this.render();
  }

  _buildHeader() {
    const panel = document.getElementById('card-panel');
    if (!panel) return;

    const header = document.getElementById('card-panel-header');
    if (!header) return;

    header.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <h3 style="margin:0;font-size:13px;">Card Library</h3>
        <span id="card-count" style="font-size:11px;color:#999;">0 cards</span>
      </div>
      <div style="display:flex;gap:4px;margin-bottom:6px;">
        <button class="card-tab${this._view === 'library' ? ' active' : ''}" data-view="library" style="flex:1;padding:3px 6px;font-size:10px;border:1px solid #ddd;border-radius:3px;cursor:pointer;background:${this._view === 'library' ? '#2563eb' : '#fff'};color:${this._view === 'library' ? '#fff' : '#333'};">Library</button>
        <button class="card-tab${this._view === 'document' ? ' active' : ''}" data-view="document" style="flex:1;padding:3px 6px;font-size:10px;border:1px solid #ddd;border-radius:3px;cursor:pointer;background:${this._view === 'document' ? '#2563eb' : '#fff'};color:${this._view === 'document' ? '#fff' : '#333'};">In Doc</button>
      </div>
      <input id="card-search" type="text" placeholder="Search cards..." value="${this._searchQuery}" style="width:100%;padding:4px 8px;border:1px solid #ddd;border-radius:4px;font-size:11px;margin-bottom:6px;box-sizing:border-box;">
      <div style="display:flex;gap:4px;margin-bottom:6px;flex-wrap:wrap;">
        <select id="card-type-filter" style="flex:1;padding:2px 4px;font-size:10px;border:1px solid #ddd;border-radius:3px;">
          <option value="">All types</option>
          <option value="snippet"${this._typeFilter === 'snippet' ? ' selected' : ''}>Snippet</option>
          <option value="text"${this._typeFilter === 'text' ? ' selected' : ''}>Text</option>
          <option value="table"${this._typeFilter === 'table' ? ' selected' : ''}>Table</option>
          <option value="spreadsheet"${this._typeFilter === 'spreadsheet' ? ' selected' : ''}>Spreadsheet</option>
          <option value="chart"${this._typeFilter === 'chart' ? ' selected' : ''}>Chart</option>
          <option value="calculation"${this._typeFilter === 'calculation' ? ' selected' : ''}>Calculation</option>
          <option value="signature"${this._typeFilter === 'signature' ? ' selected' : ''}>Signature</option>
          <option value="date-stamp"${this._typeFilter === 'date-stamp' ? ' selected' : ''}>Date Stamp</option>
          <option value="template"${this._typeFilter === 'template' ? ' selected' : ''}>Template</option>
          <option value="image"${this._typeFilter === 'image' ? ' selected' : ''}>Image</option>
        </select>
        <button id="card-new-btn" style="padding:2px 8px;font-size:10px;background:#059669;color:#fff;border:none;border-radius:3px;cursor:pointer;font-weight:600;" title="Create new library card">+ New</button>
      </div>
    `;

    this.countEl = header.querySelector('#card-count');

    // Wire events
    header.querySelectorAll('.card-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this._view = btn.dataset.view;
        this._buildHeader();
        this.render();
      });
    });

    const searchInput = header.querySelector('#card-search');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this._searchQuery = e.target.value;
        this.render();
      });
    }

    const typeFilter = header.querySelector('#card-type-filter');
    if (typeFilter) {
      typeFilter.addEventListener('change', (e) => {
        this._typeFilter = e.target.value;
        this.render();
      });
    }

    const newBtn = header.querySelector('#card-new-btn');
    if (newBtn) {
      newBtn.addEventListener('click', () => this._showCreateDialog());
    }
  }

  render() {
    if (!this.container) return;

    let cards;
    if (this._view === 'library') {
      cards = this._searchQuery
        ? this.cardEngine.searchLibrary(this._searchQuery)
        : this.cardEngine.getAllLibraryCards();
    } else {
      cards = this.cardEngine.getAllCards();
      if (this._searchQuery) {
        const q = this._searchQuery.toLowerCase();
        cards = cards.filter(c =>
          c.name.toLowerCase().includes(q) ||
          c.type.includes(q) ||
          c.tags.some(t => t.toLowerCase().includes(q))
        );
      }
    }

    if (this._typeFilter) {
      cards = cards.filter(c => c.type === this._typeFilter);
    }

    // Sort: most recently modified first
    cards.sort((a, b) => (b.modified || 0) - (a.modified || 0));

    if (this.countEl) {
      this.countEl.textContent = `${cards.length} card${cards.length !== 1 ? 's' : ''}`;
    }

    this.container.innerHTML = '';

    if (cards.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'text-align:center;padding:20px 10px;color:#999;font-size:11px;';
      empty.textContent = this._view === 'library'
        ? 'No cards in library. Click "+ New" to create one.'
        : 'No cards in current document.';
      this.container.appendChild(empty);
      return;
    }

    cards.forEach(card => {
      const item = this._createCardItem(card);
      this.container.appendChild(item);
    });
  }

  _createCardItem(card) {
    const isLibrary = this._view === 'library';
    const item = document.createElement('div');
    item.className = 'card-item' + (card.id === this.activeCardId ? ' active' : '');
    item.dataset.cardId = card.id;

    // Type icon
    const typeIcons = {
      text: '\u00B6', heading: 'H', table: '\u2637', image: '\uD83D\uDDBC',
      snippet: '\u270E', spreadsheet: '\u2637', chart: '\uD83D\uDCC8',
      calculation: '\u0192', signature: '\u270D', 'date-stamp': '\uD83D\uDCC5',
      template: '\uD83D\uDCC4',
    };

    const header = document.createElement('div');
    header.className = 'card-item-header';

    const name = document.createElement('span');
    name.className = 'card-item-name';
    name.textContent = card.name;

    const typeBadge = document.createElement('span');
    typeBadge.className = `card-item-type ${card.type}`;
    typeBadge.textContent = (typeIcons[card.type] || '') + ' ' + card.type;
    typeBadge.style.fontSize = '9px';

    header.appendChild(name);
    header.appendChild(typeBadge);
    item.appendChild(header);

    // Preview
    if (card.preview || card.data?.content) {
      const preview = document.createElement('div');
      preview.className = 'card-item-preview';
      const text = card.preview || card.data?.content || '';
      preview.textContent = text.length > 80 ? text.substring(0, 80) + '...' : text;
      item.appendChild(preview);
    }

    // Tags
    if (card.tags && card.tags.length > 0) {
      const tagsContainer = document.createElement('div');
      tagsContainer.className = 'card-item-tags';
      card.tags.forEach(tag => {
        const tagEl = document.createElement('span');
        tagEl.className = 'card-tag';
        tagEl.textContent = `#${tag}`;
        tagsContainer.appendChild(tagEl);
      });
      item.appendChild(tagsContainer);
    }

    // Action buttons
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:4px;margin-top:4px;';

    if (isLibrary) {
      // Insert button
      const insertBtn = document.createElement('button');
      insertBtn.textContent = 'Insert';
      insertBtn.style.cssText = 'padding:2px 8px;font-size:10px;background:#2563eb;color:#fff;border:none;border-radius:3px;cursor:pointer;';
      insertBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._insertCardIntoDoc(card);
      });
      actions.appendChild(insertBtn);

      // Edit button
      const editBtn = document.createElement('button');
      editBtn.textContent = 'Edit';
      editBtn.style.cssText = 'padding:2px 8px;font-size:10px;background:#f59e0b;color:#fff;border:none;border-radius:3px;cursor:pointer;';
      editBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showEditDialog(card);
      });
      actions.appendChild(editBtn);

      // Delete button
      const delBtn = document.createElement('button');
      delBtn.textContent = '\u2715';
      delBtn.style.cssText = 'padding:2px 6px;font-size:10px;background:#dc2626;color:#fff;border:none;border-radius:3px;cursor:pointer;margin-left:auto;';
      delBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (confirm(`Delete "${card.name}" from library?`)) {
          this.cardEngine.removeLibraryCard(card.id);
        }
      });
      actions.appendChild(delBtn);
    } else {
      // Save to library button (for document cards)
      const saveBtn = document.createElement('button');
      saveBtn.textContent = 'Save to Library';
      saveBtn.style.cssText = 'padding:2px 8px;font-size:10px;background:#059669;color:#fff;border:none;border-radius:3px;cursor:pointer;';
      saveBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.cardEngine.saveToLibrary(card);
      });
      actions.appendChild(saveBtn);
    }

    item.appendChild(actions);

    item.addEventListener('click', () => {
      this.activeCardId = card.id;
      this.render();
    });

    return item;
  }

  _insertCardIntoDoc(card) {
    const data = card.data || {};
    const headers = data.headers || [];
    const colTypes = data.colTypes || [];
    const cardName = card.name;

    // If no headers (old card format), just insert directly
    if (headers.length === 0) {
      this.cardEngine._emit('card:insert-request', card);
      return;
    }

    // Build options based on card data structure
    const options = [];
    const textCols = colTypes.map((t, i) => t === 'text' ? i : -1).filter(i => i >= 0);
    const numCols = colTypes.map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);

    // Lookup options (text → number pairs)
    if (textCols.length > 0 && headers.length >= 2) {
      for (const tc of textCols) {
        for (let oc = 0; oc < headers.length; oc++) {
          if (oc === tc) continue;
          options.push({ icon: '\uD83D\uDD0D', label: `Look up by ${headers[tc]} \u2192 get ${headers[oc]}`, insert: `@Card(${cardName},feed)  @Card(${cardName},result)`, feedCol: tc, resultCol: oc });
        }
      }
    }

    // Feed options per column
    headers.forEach((h, i) => {
      options.push({ icon: '\uD83D\uDCCB', label: `${h} column as list`, insert: `@Card(${cardName},${h})` });
    });

    // Full table
    options.push({ icon: '\uD83D\uDCCA', label: 'Full table', insert: `{${cardName}}` });

    // Aggregates
    numCols.forEach(nc => {
      options.push({ icon: '\u2795', label: `Sum of ${headers[nc]}`, insert: `@Card(${cardName},sum)` });
      options.push({ icon: '\uD83D\uDCC8', label: `Average of ${headers[nc]}`, insert: `@Card(${cardName},avg)` });
    });

    // Show chooser
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:999999;display:flex;align-items:center;justify-content:center;';

    const dlg = document.createElement('div');
    dlg.style.cssText = 'background:#fff;border-radius:14px;box-shadow:0 12px 48px rgba(0,0,0,0.25);width:440px;max-height:70vh;overflow-y:auto;padding:24px 28px;font-family:Arial,sans-serif;';

    let optionsHtml = options.map(opt => `
      <div class="card-use-option" data-insert="${opt.insert.replace(/"/g, '&quot;')}" style="display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:8px;cursor:pointer;">
        <span style="font-size:18px;">${opt.icon}</span>
        <div style="font-size:13px;color:#333;">${opt.label}</div>
      </div>
    `).join('');

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;font-size:16px;color:#1a1a1a;">Insert "${cardName}"</h3>
        <button class="card-use-close" style="background:none;border:none;font-size:18px;cursor:pointer;color:#999;">&times;</button>
      </div>
      <p style="font-size:13px;color:#666;margin:0 0 16px;">What do you want from this card?</p>
      <div style="display:flex;flex-direction:column;gap:6px;">${optionsHtml}</div>
    `;

    overlay.appendChild(dlg);
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    dlg.querySelector('.card-use-close').addEventListener('click', () => overlay.remove());

    dlg.querySelectorAll('.card-use-option').forEach(item => {
      item.addEventListener('mouseenter', () => { item.style.borderColor = '#2563eb'; item.style.background = '#f8faff'; });
      item.addEventListener('mouseleave', () => { item.style.borderColor = '#e5e7eb'; item.style.background = '#fff'; });
      item.addEventListener('click', () => {
        overlay.remove();
        card._insertText = item.dataset.insert;
        this.cardEngine._emit('card:insert-request', card);
      });
    });
  }

  _showCreateDialog() {
    const existing = document.getElementById('card-create-dialog');
    if (existing) existing.remove();

    const dialog = document.createElement('div');
    dialog.id = 'card-create-dialog';
    dialog.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.35);z-index:9000;display:flex;align-items:center;justify-content:center;';
    dialog.innerHTML = `
      <div style="background:#fff;border-radius:12px;padding:24px;width:450px;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <h3 style="margin:0;font-size:16px;">Create Library Card</h3>
          <button id="card-create-close" style="border:none;background:none;font-size:20px;cursor:pointer;">&times;</button>
        </div>
        <div style="display:flex;flex-direction:column;gap:10px;">
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Card Type</label>
            <select id="card-create-type" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;">
              <option value="snippet">Snippet (reusable text)</option>
              <option value="date-stamp">Date Stamp (auto date)</option>
              <option value="signature">Signature</option>
              <option value="calculation">Calculation (formula)</option>
              <option value="template">Document Template</option>
              <option value="table">Table / Data Card</option>
              <option value="text">Text Block</option>
            </select>
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Name</label>
            <input id="card-create-name" type="text" placeholder="e.g., Patient Header, Signature Block" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;box-sizing:border-box;">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Tags (comma-separated)</label>
            <input id="card-create-tags" type="text" placeholder="e.g., medical, billing, template" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;box-sizing:border-box;">
          </div>
          <div id="card-create-content-area">
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Content</label>
            <textarea id="card-create-content" placeholder="Card content — for snippets, type your reusable text here..." style="width:100%;height:120px;padding:8px;border:1px solid #ddd;border-radius:4px;font-size:13px;resize:vertical;box-sizing:border-box;font-family:inherit;"></textarea>
          </div>
          <div id="card-create-format-area" style="display:none;">
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Date Format</label>
            <select id="card-create-format" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;">
              <option value="locale">Local format</option>
              <option value="us">US (MM/DD/YYYY)</option>
              <option value="eu">EU (DD/MM/YYYY)</option>
              <option value="iso">ISO (YYYY-MM-DD)</option>
              <option value="long">Long (March 15, 2026)</option>
              <option value="datetime">Date + Time</option>
            </select>
          </div>
          <div id="card-create-formula-area" style="display:none;">
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Formula</label>
            <input id="card-create-formula" type="text" placeholder="e.g., SUM(Billing.B1:B10)" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;box-sizing:border-box;font-family:'Fira Code',monospace;">
          </div>
          <button id="card-create-save" style="padding:8px 20px;background:#059669;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;margin-top:4px;">Create Card</button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    // Toggle content areas based on type
    const typeSelect = document.getElementById('card-create-type');
    const contentArea = document.getElementById('card-create-content-area');
    const formatArea = document.getElementById('card-create-format-area');
    const formulaArea = document.getElementById('card-create-formula-area');

    typeSelect.addEventListener('change', () => {
      const t = typeSelect.value;
      contentArea.style.display = (t === 'snippet' || t === 'text' || t === 'template' || t === 'table') ? '' : 'none';
      formatArea.style.display = (t === 'date-stamp') ? '' : 'none';
      formulaArea.style.display = (t === 'calculation') ? '' : 'none';
    });

    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.remove(); });
    document.getElementById('card-create-close').addEventListener('click', () => dialog.remove());

    document.getElementById('card-create-save').addEventListener('click', () => {
      const type = typeSelect.value;
      const name = document.getElementById('card-create-name').value.trim();
      if (!name) { alert('Please enter a card name.'); return; }

      const tags = document.getElementById('card-create-tags').value
        .split(',').map(t => t.trim()).filter(Boolean);

      let data = {};
      let preview = '';

      if (type === 'snippet' || type === 'text') {
        const content = document.getElementById('card-create-content').value;
        data = { content };
        preview = content.substring(0, 100);
      } else if (type === 'date-stamp') {
        data = { format: document.getElementById('card-create-format').value };
        preview = 'Auto date: ' + data.format;
      } else if (type === 'calculation') {
        const formula = document.getElementById('card-create-formula').value.trim();
        data = { formula };
        preview = '=' + formula;
      } else if (type === 'signature') {
        data = { dataUrl: '' }; // Will be captured via canvas later
        preview = 'Signature card';
      } else if (type === 'template') {
        const content = document.getElementById('card-create-content').value;
        data = { description: content, slots: [] };
        preview = content.substring(0, 100);
      }

      this.cardEngine.createLibraryCard(type, { name, tags, data, preview });
      dialog.remove();
    });
  }

  _showEditDialog(card) {
    const existing = document.getElementById('card-edit-dialog');
    if (existing) existing.remove();

    const dialog = document.createElement('div');
    dialog.id = 'card-edit-dialog';
    dialog.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.35);z-index:9000;display:flex;align-items:center;justify-content:center;';
    dialog.innerHTML = `
      <div style="background:#fff;border-radius:12px;padding:24px;width:450px;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <h3 style="margin:0;font-size:16px;">Edit Card: ${card.name}</h3>
          <button id="card-edit-close" style="border:none;background:none;font-size:20px;cursor:pointer;">&times;</button>
        </div>
        <div style="display:flex;flex-direction:column;gap:10px;">
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Name</label>
            <input id="card-edit-name" type="text" value="${card.name}" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;box-sizing:border-box;">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Tags</label>
            <input id="card-edit-tags" type="text" value="${card.tags.join(', ')}" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;box-sizing:border-box;">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Content</label>
            <textarea id="card-edit-content" style="width:100%;height:120px;padding:8px;border:1px solid #ddd;border-radius:4px;font-size:13px;resize:vertical;box-sizing:border-box;font-family:inherit;">${card.data?.content || card.data?.formula || card.preview || ''}</textarea>
          </div>
          <button id="card-edit-save" style="padding:8px 20px;background:#2563eb;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Save Changes</button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.remove(); });
    document.getElementById('card-edit-close').addEventListener('click', () => dialog.remove());

    document.getElementById('card-edit-save').addEventListener('click', () => {
      const name = document.getElementById('card-edit-name').value.trim();
      const tags = document.getElementById('card-edit-tags').value
        .split(',').map(t => t.trim()).filter(Boolean);
      const content = document.getElementById('card-edit-content').value;

      const updates = { name, tags };

      if (card.type === 'snippet' || card.type === 'text') {
        updates.data = { ...card.data, content };
        updates.preview = content.substring(0, 100);
      } else if (card.type === 'calculation') {
        updates.data = { ...card.data, formula: content };
        updates.preview = '=' + content;
      } else {
        updates.preview = content.substring(0, 100);
      }

      this.cardEngine.updateLibraryCard(card.id, updates);
      dialog.remove();
    });
  }

  setActiveCard(cardId) {
    this.activeCardId = cardId;
    this.render();
  }
}
